'use client';

import React, { useMemo, type ReactElement, type ReactNode } from 'react';
import { WitroMobileNavOffset } from '@/features/shop/components/witro-mobile-nav.component';
import classNames from 'classnames';
import Appbar, {
  type AppbarProps,
} from '@/common/components/appbar/appbar.component';
import ResponsiveAppbar from '@/common/components/appbar/responsive-appbar.component';
import TopMessageBar from '@/common/components/top-message-bar';

export interface LayoutProps {
  className?: string;
  children: ReactNode;
  hideAppBar?: boolean;
  withFixedButton?: boolean;
  appbar?: AppbarProps;
  childHeight?: string;
  responsiveAppbar?: boolean;
  appbarType?: string;
}

export default function Layout({
  className,
  children,
  hideAppBar = false,
  withFixedButton,
  appbar,
  childHeight,
  responsiveAppbar = false,
  appbarType,
}: LayoutProps): ReactElement {
  const height = useMemo(() => {
    if (childHeight && childHeight.length > 0) return childHeight;

    if (withFixedButton && !hideAppBar) {
      return 'calc(100% - 152px)';
    }
    if (withFixedButton && hideAppBar) {
      return 'calc(100% - 92px)';
    }
    if (!withFixedButton && !hideAppBar) {
      return 'calc(100% - 60px)';
    }
    return '100%';
  }, [hideAppBar, withFixedButton, childHeight]);

  return (
    <div
      className={classNames(
        'flex h-full w-full flex-col items-start justify-start lg:bg-none',
        className
      )}
    >
      {responsiveAppbar && <TopMessageBar />}
      {!hideAppBar && !responsiveAppbar && <Appbar {...appbar} />}
      {responsiveAppbar && (
        <div
          className={classNames(
            'w-full shrink-0',
            appbar?.fixedHeader && 'sticky top-0 z-[100]'
          )}
        >
          <ResponsiveAppbar
            appbar={{ ...appbar, fixedHeader: false }}
            appbarType={appbarType}
          />
        </div>
      )}
      <div
        style={responsiveAppbar && !childHeight ? undefined : { height }}
        className={classNames(
          !responsiveAppbar && appbar?.fixedHeader ? 'pt-[146px] lg:pt-0' : '',
          responsiveAppbar && !childHeight
            ? 'min-h-0 w-full flex-1 lg:px-0'
            : 'h-full w-full lg:px-0'
        )}
      >
        {children}
        <WitroMobileNavOffset />
      </div>
    </div>
  );
}
